<template>
  <header
    class="booking-header"
    data-component="booking-header"
  >
    <div class="booking-header-wrapper container">
      <div class="booking-header-left">
        <a
          v-if="!isApp"
          class="icon-fan-logo"
          aria-label="logo"
          :href="homeUrl"
          @click="cartRemoveEventTag"
          @keydown.space.prevent="cartRemoveEventTag"
        />
        <a
          v-else
          class="icon-close-white"
          href="?status=closed"
        />
        <div class="booking-header-left-cta booking-header-item">
          <language-switcher :languages="languages">
            <div
              slot-scope="{ activate, displayName }"
              class="language-switcher-wrapper"
            >
              <button
                class="language"
                @click="activate"
                @keydown.space.prevent="activate"
              >
                <span class="icon-globe" />
                <span class="language-drop-down">{{ displayName }}</span>
                <span class="icon-chevron-down" />
              </button>
            </div>
          </language-switcher>
          <currency-switcher>
            <button
              slot-scope="{ activate }"
              class="currency-drop-down booking-header-item"
              :class="isTravelAgent ? 'no-border' : ''"
              @click="activate"
              @keydown.space.prevent="activate"
            >
              {{ currentCurrency }}
              <span class="icon-chevron-down" />
            </button>
          </currency-switcher>
        </div>
      </div>
      <div
        v-if="!isTravelAgent"
        class="booking-header-right"
      >
        <auth-activator v-if="!haveProfileData">
          <template slot-scope="{ activate }">
            <button
              class="btn-wrapper booking-header-item"
              @click="activate('login', $event.target)"
            >
              <div class="booking-login">
                {{ tc("auth.logIn") }}
              </div>
              <div
                class="signin"
                :aria-label="tc('auth.logIn')"
                tab-index="0"
              />
            </button>
          </template>
        </auth-activator>
        <div
          v-else
          class="btn-wrapper booking-header-item"
          :class="{ 'booking-header-item--login': haveProfileData }"
        >
          <div class="booking-login">
            <a
              :href="userProfileUrl"
            >
              <span class="desktop-up">
                {{
                  tc("auth.welcomeText", {
                    name: `${user.title} ${user.lastName}`,
                  })
                }}
              </span>
              <div
                class="signin"
                :aria-label="tc('auth.logIn')"
                tab-index="0"
              />
            </a>
          </div>
        </div>

        <div
          v-if="haveProfileData"
          class="btn-wrapper booking-header-item booking-header-item--logout"
        >
          <div class="btn-wrapper booking-header-item">
            <div class="booking-login">
              <button
                class="logout-button"
                @click.prevent="logout"
              >
                <svg
                  viewBox="0 0 24 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M16 17.5L21 12.5L16 7.5"
                    stroke-width="1.2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M21 12.5H9"
                    stroke-width="1.2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M9 21.5H5C4.46957 21.5 3.96086 21.2893 3.58579 20.9142C3.21071 20.5391 3 20.0304 3 19.5V5.5C3 4.96957 3.21071 4.46086 3.58579 4.08579C3.96086 3.71071 4.46957 3.5 5 3.5H9"
                    stroke-width="1.2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <span class="desktop-up">{{ additionalData.logoutText }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
  import { mapActions, mapGetters, mapState } from 'vuex'
  import currencySwitcher from '../../../currency-switcher/currency-switcher.vue'

  export default {
    name: 'BookingHeader',
    components: { currencySwitcher },
    provide() {
      return {
        skipBookingTracking: this.skipBookingTracking,
      }
    },
    props: {
      languages: {
        type: Array,
        default: () => [],
      },
      additionalData: {
        type: Object,
        required: true,
      },
      skipBookingTracking: {
        type: Boolean,
        default: false,
      },
    },
    data: () => ({
      inRoute: false,
      bookingComponent: null,
      headerWrapper: null,
      bodySection: null,
      isFirstLoad: true,
      oldScroll: null,
      isScrolling: undefined,
      isScrollingUp: false,
    }),
    computed: {
      ...mapGetters('i18n', ['tc']),
      ...mapGetters('currency', ['currentCurrency']),
      ...mapGetters('responsive', ['currentBreakpoint']),
      ...mapState('booking', ['query']),
      ...mapState('auth', ['user']),
      ...mapGetters('auth', ['haveProfileData']),
      ...mapState('global', { currentLang: 'lang' }),
      ...mapGetters('global', ['isApp']),
      ...mapGetters('booking', ['getBookingTrackingV2', 'getQuery']),
      ...mapState('global', ['isFanClubBooking']),
      homeUrl() {
        return `/${this.currentLang}`
      },
      isTravelAgent() {
        return !!this.query.iataNumber || this.isFanClubBooking
      },
      userProfileUrl() {
        if (!this.user.loyalLevel) return this.additionalData.greetingsLink

        if (['FE', 'FE+', 'PER'].includes(this.user.loyalLevel)) {
          return (
            this.additionalData.eliteDashboardLink
            || this.additionalData.greetingsLink
          )
        }
        if (['FER+'].includes(this.user.loyalLevel)) {
          return (
            this.additionalData.residencesDashboardLink
            || this.additionalData.greetingsLink
          )
        }
        if (['FS'].includes(this.user.loyalLevel)) {
          return (
            this.additionalData.selectTierDashboardLink
            || this.additionalData.greetingsLink
          )
        }

        return this.additionalData.greetingsLink
      },
    },
    watch: {
      $route: {
        handler(to, from) {
          this.inRoute = true
          const timeout = setTimeout(() => {
            this.inRoute = false
            clearTimeout(timeout)
          }, 1000)
        },
        deep: true,
      },
      currentBreakpoint() {
        this.handleHeader()
      },
      isFanClubBooking(newValue) {
        if (newValue) {
          this.agentGetProfile()
        }
      }
    },
    mounted() {
      this.defineElements()
      this.bindListeners()
      this.initUser()
    },
    methods: {
      ...mapActions('auth', ['getProfile', 'logout']),
      ...mapActions('agentAuth', { agentGetProfile: 'getProfile' }),
      async initUser() {
        if (this.user && !this.haveProfileData && !this.isFanClubBooking) {
          await this.getProfile()
        }
        if (this.isFanClubBooking) {
          await this.agentGetProfile()
        }
      },
      defineElements() {
        this.bookingComponent = this.$el.parentNode
        this.headerWrapper = this.$el
      },
      bindListeners() {
        this.bookingComponent.addEventListener('scroll', () => {
          if (!this.inRoute) {
            this.defineScrollingDirection()
            this.handleHeader()
            this.scrollingTimeout()
          }
        })
      },
      defineScrollingDirection() {
        this.bodySection = this.$el.nextElementSibling
        if (this.oldScroll < this.bodySection.getBoundingClientRect().top) {
          this.isScrollingUp = true
        } else {
          this.isScrollingUp = false
        }
        this.oldScroll = this.bodySection.getBoundingClientRect().top
      },
      handleHeader() {
        const headerExtension = this.bookingComponent.querySelector(
          '.booking-header-extension'
        )
        const headerHeight = this.headerWrapper.offsetHeight
        if (this.isScrollingUp) {
          this.headerWrapper.classList.remove('hidden')
          headerExtension.classList.add('sticky', 'as-sub-header')
          headerExtension.style.top = `${headerHeight}px`
        } else {
          this.headerWrapper.classList.add('hidden')
          headerExtension.classList.add('sticky')
          headerExtension.classList.remove('as-sub-header')
          headerExtension.style.top = 0
        }
      },
      scrollingTimeout() {
        if (this.isFirstLoad) {
          this.isFirstLoad = false
          return
        }
        window.clearTimeout(this.isScrolling)
        this.isScrolling = setTimeout(() => {
          this.restoreHeader()
        }, 1000)
      },
      restoreHeader() {
        const headerExtension = this.bookingComponent.querySelector(
          '.booking-header-extension'
        )
        this.headerWrapper.classList.remove('hidden')
        headerExtension.classList.add('sticky')
        headerExtension.style.top = `${this.headerWrapper.offsetHeight}px`
      },
      unbindListeners() {
        this.bookingComponent.removeEventListener('scroll', () => {
          this.handleHeader()
          this.scrollingTimeout()
        })
      },
      cartRemoveEventTag() {
        this.$track(
          this.getBookingTrackingV2(this.getQuery, { event: 'cartRemove' })
        )
      },
    },
  }
</script>
